import { convertMoneyStringToNumber } from "@shared/modules/financials/utils/price-utils";
import {
  GtagBasketType,
  GtagItemGroupType,
  GtagTicketType,
} from "../types/analytics-types";

export type ItemParametersType = {
  item_id: string;
  item_name: string;
  item_brand?: string;
  item_category: string;
  price?: number;
  quantity?: number;
  currency?: string;
};

export enum GoogleEventNames {
  ViewItem = "view_item",
  ViewContent = "view_content",
  AddToCart = "add_to_cart",
  RemoveFromCart = "remove_from_cart",
  BeginCheckout = "begin_checkout",
  PostPurchase = "purchase",
  AWConversion = "conversion",
  ConsentGranted = "consent_granted",
  ConsentRevoked = "consent_revoked",
}

/**
 * Build item list to send to google
 * @param basket, venue
 *
 */
export const buildItemList = (
  basketOrTTSet: GtagBasketType | Array<GtagTicketType>,
  name?: string,
  currency?: string
): ItemParametersType[] => {
  // use item_groups or tickettype_set
  const tt =
    basketOrTTSet && "item_groups" in basketOrTTSet
      ? basketOrTTSet.item_groups || []
      : (basketOrTTSet as Array<GtagTicketType>);

  return tt.map((group: GtagItemGroupType | GtagTicketType) => {
    return {
      item_id: group.item_id,
      item_category: group.item_category,
      item_name: group.item_name,
      item_variant: group.item_variant,
      price:
        "price" in group ? convertMoneyStringToNumber(group.price) : undefined,
      quantity: "quantity" in group ? group.quantity : 1,
      item_brand: name,
      currency,
    };
  });
};

export const viewItemsAnalyticsData = (
  ttset: GtagBasketType | Array<GtagTicketType>,
  name?: string,
  currency?: string
) => {
  const items = buildItemList(ttset, name, currency);
  // Update the ticket total to come from built items above to accomdate tickets and baskets
  const ticketTypesTotal = items?.reduce((prev, curr) => {
    return prev + (curr.price || 0);
  }, 0);

  return {
    currency,
    value: ticketTypesTotal,
    items,
  };
};

export const addToCartAnalyticsData = (
  group: GtagItemGroupType,
  count: number,
  name?: string,
  currency?: string
) => {
  return {
    currency,
    value: convertMoneyStringToNumber(group.subtotal),
    items: [
      {
        item_id: group.item_id,
        item_brand: name,
        item_name: group.item_name,
        item_variant: group.item_variant,
        item_category: group.item_category,
        quantity: count,
        price: convertMoneyStringToNumber(group.price),
      },
    ],
  };
};

export const removeFromCartAnalyticsData = () => {
  return { event_category: "ecommerce" };
};

export const beginCheckoutAnalyticsData = (
  basket: GtagBasketType,
  name?: string,
  currency?: string
) => {
  // Build e-commerce items for each item groups
  const items = buildItemList(basket, name, currency);
  return {
    currency: basket.currency,
    value: convertMoneyStringToNumber(basket.total_price),
    coupon: basket.discount_code,
    items,
  };
};

export const postPurchaseAnalyticsData = (
  basket: GtagBasketType,
  name?: string,
  currency?: string
) => {
  // Build e-commerce items for each item groups
  const items = buildItemList(basket, name, currency);
  return {
    transaction_id: basket.id, // Transaction ID. Required.
    affiliation: name,
    value: convertMoneyStringToNumber(basket.total_price),
    currency: basket.currency,
    // basket.taxes & basket.shipping_cost is always a string no matter what. Adjusting the type definition is a massive rabbit hole:
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    tax: convertMoneyStringToNumber(basket.taxes),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    shipping: convertMoneyStringToNumber(basket.shipping_cost),
    coupon: basket.discount_code,
    items,
  };
};
